import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  input,
  Input,
  model,
  output,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { BCTopHeaderFiltersComponent } from './components/bc-top-header-filters/bc-top-header-filters.component';
import { MobileHeaderFiltersComponent } from './components/mobile-header-filters/mobile-header-filters.component';
import { MobileRightMenuComponent } from './components/mobile-right-menu/mobile-right-menu.component';
import { SideMenuBarComponent } from './components/side-menu-bar/side-menu-bar.component';
import { SideMenuDrawerComponent } from './components/side-menu-drawer/side-menu-drawer.component';
import { SideMenuComponent } from './components/side-menu/side-menu.component';
import { TopHeaderComponent } from './components/top-header/top-header.component';
import { MenuTool } from './interfaces/MenuTool.interface';
import { NavigationItem } from './interfaces/navigationList.interface';
import { PageContainerConfigurations } from './interfaces/PageContainerConfiguration.interface';
import { FiltersLocation, FiltersRegion } from './interfaces/region.interface';
import { PageContainerService } from './services/page-container.service';
import { ViewDetectionService } from './services/view-detection.service';

@Component({
  selector: 'z-pages-container',
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    TopHeaderComponent,
    SideMenuComponent,
    SideMenuBarComponent,
    SideMenuDrawerComponent,
    MobileRightMenuComponent,
    MobileHeaderFiltersComponent,
    BCTopHeaderFiltersComponent,
  ],
  templateUrl: './pages-container.component.html',
  styleUrls: ['./pages-container.component.scss'],
})
export class PagesContainerComponent {
  @Input({ required: true }) navigationList: NavigationItem[] = [];
  dashboardRoutes = input.required<NavigationItem[]>();
  settingsRoutes = input.required<NavigationItem>();
  tools = input.required<MenuTool[]>();
  configurations = input.required<PageContainerConfigurations>();
  regions = input.required<FiltersRegion[]>();
  locations = input.required<FiltersLocation[]>();
  locationsFilters = output<FiltersLocation[]>();
  selectedRegions = model<FiltersRegion[]>([]);
  selectedLocations = model<FiltersLocation[]>([]);

  isFiltersMenuOpened = signal(false);

  gridView$ = this.viewDetection.gridView.pipe(
    tap(view => {
      if (view === 'large' || view === 'xLarge') {
        this.pageContainerService.expandMenuDrawer$.next(false);
        this.pageContainerService.expandFilterDrawer$.next(false);
      }
    }),
  );

  menuExpanded$: Observable<boolean> = this.pageContainerService.menuExpanded;
  expandMenuDrawer$ = this.pageContainerService.expandMenuDrawer$;
  expandFilterDrawer$ = this.pageContainerService.expandFilterDrawer$.pipe(
    tap(x => {
      if (x) this.pageContainerService.expandMenuDrawer$.next(!x);

      if (x !== this.isFiltersMenuOpened()) {
        this.isFiltersMenuOpened.set(x);
      }
    }),
  );

  constructor(
    readonly pageContainerService: PageContainerService,
    readonly viewDetection: ViewDetectionService,
    readonly cd: ChangeDetectorRef,
    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly navigationService: NavigationService,
  ) {}

  ngOnInit() {
    this.pageContainerService.homePageConfig.set(
      this.configurations().homeButton,
    );
    this.pageContainerService.profileConfig.set(this.configurations().profile);
    this.pageContainerService.initializeRouterEvent();

    const currentUrl = this.router.url;
    this.pageContainerService.handleInitialNavigation(currentUrl);
  }

  ngAfterViewInit() {
    this.checkSetFiltersDefault();
  }

  filter() {
    this.checkSetFiltersDefault();
    this.locationsFilters.emit(this.selectedLocations());
  }

  expandFilters() {
    this.isFiltersMenuOpened.update(x => !x);
    this.pageContainerService.expandFilterDrawer$.next(
      this.isFiltersMenuOpened(),
    );
  }

  checkSetFiltersDefault() {
    if (!this.configurations().hasHeaderFilters) {
      return; // Exit early if no filters are needed
    }

    // Set default region if none is selected
    if (this.selectedRegions().length === 0 && this.regions().length > 0) {
      this.selectedRegions.set([this.regions()[0]]);
    }

    // Set default location if none is selected and a region is available
    if (
      this.selectedLocations().length === 0 &&
      this.selectedRegions().length > 0
    ) {
      const regionId = this.selectedRegions()[0]?.id;
      const defaultLocation = this.locations().find(
        loc => loc.regionId === regionId,
      );

      if (defaultLocation) {
        this.selectedLocations.set([defaultLocation]);
      } else {
        throw new Error(
          `Cannot find a location for region with ID: ${regionId}`,
        );
      }
    }

    this.cd.detectChanges();
  }
}
